/**
 * The Feedback contract — one flat router, `feedbackContract` (`/feedback`), covering direct
 * feedback, 360°-style requests, and the response to a request. See
 * `docs/plans/performance-management/feedback-1on1s-plan.md`'s "Contract & API" section.
 *
 * ANONYMITY IS A PERMISSION, NOT A CODE CONVENTION. `feedbackSchema.authorPersonId` is nullable on
 * the wire because the *value* the route returns is what varies — `null` for a caller without
 * `feedback.identity:read`, the real id for one with it — never because the column itself is
 * nullable in a different sense. The author is always stored (see the plan's Data model section);
 * this schema only has to describe what the wire can legitimately contain.
 *
 * `createRequests` IS DELIBERATELY PLURAL. One-at-a-time creation would leave a partially-requested
 * 360° that `FeedbackRequest`'s "N of M responded" gate reads as incomplete forever — see the plan's
 * `@@unique([orgId, reviewId, respondentPersonId])` note. The body carries every respondent; the
 * route writes all of their rows in one transaction under one `Idempotency-Key`.
 *
 * `reactToFeedback` HAS NO DEDICATED REACTION TABLE. Unlike `recognitionContract`'s
 * `reactToRecognition` (backed by `RecognitionReaction`, with an `@@unique` that makes a double-tap
 * naturally idempotent), the Data model section lists exactly 9 new tables and none of them is a
 * per-person feedback-reaction join. `thanksCount` on `Feedback` is a denormalized counter instead —
 * see routes/feedback.ts's header for how the `Idempotency-Key` middleware, not a database
 * constraint, is what keeps a retried "Say Thanks" from double-counting.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  FEEDBACK_KINDS,
  FEEDBACK_RELATIONSHIPS,
  FEEDBACK_REQUEST_STATUSES,
  FEEDBACK_VISIBILITIES,
} from '../enums.js';
import {
  dateOnlySchema,
  errorBodySchema,
  instantSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

// ---------------------------------------------------------------------------
// Shared enum schemas
// ---------------------------------------------------------------------------

export const feedbackKindSchema = z.enum(FEEDBACK_KINDS);
export const feedbackVisibilitySchema = z.enum(FEEDBACK_VISIBILITIES);
export const feedbackRelationshipSchema = z.enum(FEEDBACK_RELATIONSHIPS);
export const feedbackRequestStatusSchema = z.enum(FEEDBACK_REQUEST_STATUSES);

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

/**
 * `authorPersonId`/`authorName` are both nullable for the reason spelled out in this file's header —
 * omitted-as-null unless the caller holds `feedback.identity:read`, never nulled in the database.
 */
export const feedbackSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  subjectPersonId: uuidSchema,
  subjectName: z.string(),
  authorPersonId: uuidSchema.nullable(),
  authorName: z.string().nullable(),
  isAnonymous: z.boolean(),
  kind: feedbackKindSchema,
  visibility: feedbackVisibilitySchema,
  body: z.string(),
  tags: z.array(z.string()),
  competencyId: uuidSchema.nullable(),
  /**
   * Null when the row answers no request — AND when the author is hidden from the caller (an
   * anonymous row read without `feedback.identity:read`, or any read through the AI copilot): a
   * request names exactly one respondent, so returning its id would re-identify the author through
   * `getRequest`.
   */
  feedbackRequestId: uuidSchema.nullable(),
  reviewId: uuidSchema.nullable(),
  goalId: uuidSchema.nullable(),
  dlpFlagged: z.boolean(),
  /** Denormalized "Say Thanks" tally — see this file's header on why there is no reaction table. */
  thanksCount: z.number().int().nonnegative(),
  createdAt: instantSchema,
});
export type Feedback = z.infer<typeof feedbackSchema>;

export const feedbackRequestSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  subjectPersonId: uuidSchema,
  subjectName: z.string(),
  requestedByPersonId: uuidSchema,
  requestedByName: z.string(),
  /**
   * Null when the respondent is hidden from the caller. A request is anonymous when its relationship
   * is anonymity-mandatory (`isAnonymityMandatory` — direct_report, skip_level) or it was answered
   * anonymously; its respondent is then shown only to the respondent themself and to
   * `feedback.identity:read` holders — never through the AI copilot, whatever the caller's grants.
   * `status` stays, so a subject's or requester's "x of y responded" count keeps working.
   */
  respondentPersonId: uuidSchema.nullable(),
  /** Null exactly when `respondentPersonId` is — see above. */
  respondentName: z.string().nullable(),
  reviewId: uuidSchema.nullable(),
  cycleId: uuidSchema.nullable(),
  relationship: feedbackRelationshipSchema,
  status: feedbackRequestStatusSchema,
  message: z.string().nullable(),
  dueOn: dateOnlySchema.nullable(),
  /** Also null while `respondentPersonId` is hidden — it would line up with the anonymous answer's
   * `createdAt`. */
  respondedAt: instantSchema.nullable(),
  createdAt: instantSchema,
});
export type FeedbackRequest = z.infer<typeof feedbackRequestSchema>;

export const createRequestsResponseSchema = z.object({
  items: z.array(feedbackRequestSchema),
});

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export const listFeedbackQuerySchema = paginationQuerySchema.extend({
  /** Feedback received. Defaults to the caller when neither this nor authorPersonId is set. */
  subjectPersonId: uuidSchema.optional(),
  /** Feedback given — "Given Feedback" tab. Only the caller's own id, unless the caller holds
   * `feedback.identity:read` (never through the AI copilot): anyone else's is a 404. */
  authorPersonId: uuidSchema.optional(),
  /** `visibility: 'public'` with no subjectPersonId/authorPersonId is the Recognition-Wall-shaped
   * trick `leave.ts`'s `listTypes` uses for an org-wide catalogue read. */
  visibility: feedbackVisibilitySchema.optional(),
  kind: feedbackKindSchema.optional(),
});

export const createFeedbackBodySchema = z.object({
  subjectPersonId: uuidSchema,
  kind: feedbackKindSchema,
  visibility: feedbackVisibilitySchema,
  body: z.string().trim().min(1).max(4000),
  tags: z.array(z.string().trim().min(1).max(40)).max(10).default([]),
  isAnonymous: z.boolean().default(false),
  competencyId: uuidSchema.optional(),
  /** Answering a request: the caller must be its respondent (404 otherwise), it must be pending,
   * and `subjectPersonId` must be its subject. Subject review and anonymity then come from the
   * request, exactly as `submitRequestResponse` does it — `reviewId` here is ignored. */
  feedbackRequestId: uuidSchema.optional(),
  /** Without a request: a review OF the subject (422 otherwise), attachable only by its manager of
   * record or a `review:manage` holder over the subject (404 otherwise). */
  reviewId: uuidSchema.optional(),
  goalId: uuidSchema.optional(),
});

export const listRequestsQuerySchema = paginationQuerySchema.extend({
  subjectPersonId: uuidSchema.optional(),
  respondentPersonId: uuidSchema.optional(),
  status: feedbackRequestStatusSchema.optional(),
  reviewId: uuidSchema.optional(),
});

const requestRespondentSchema = z.object({
  personId: uuidSchema,
  relationship: feedbackRelationshipSchema,
});

/** Deliberately plural — see this file's header. */
export const createRequestsBodySchema = z.object({
  subjectPersonId: uuidSchema,
  reviewId: uuidSchema.optional(),
  cycleId: uuidSchema.optional(),
  dueOn: dateOnlySchema.optional(),
  message: z.string().trim().max(1000).optional(),
  respondents: z.array(requestRespondentSchema).min(1).max(20),
});

export const submitRequestResponseBodySchema = z.object({
  body: z.string().trim().min(1).max(4000),
  visibility: feedbackVisibilitySchema.default('manager_shared'),
  tags: z.array(z.string().trim().min(1).max(40)).max(10).default([]),
  isAnonymous: z.boolean().default(false),
  competencyId: uuidSchema.optional(),
});

export const declineRequestBodySchema = z.object({
  reason: z.string().trim().max(500).optional(),
});

/** PRF-13 — upward/skip-level reviewers are the org chart, not a nomination: every one of the
 * subject's direct reports (upward) and/or grand-reports (skip-level), for whichever reviewer
 * type(s) the cycle has enabled. `reviewerTypes` omitted means "every type this cycle enabled". */
export const createUpwardSkipLevelRequestsBodySchema = z.object({
  subjectPersonId: uuidSchema,
  cycleId: uuidSchema,
  reviewerTypes: z
    .array(z.enum(['direct_report', 'skip_level']))
    .min(1)
    .max(2)
    .optional(),
});

export const createUpwardSkipLevelRequestsResponseSchema = z.object({
  items: z.array(feedbackRequestSchema),
  /** Already-requested respondents (the @@unique on reviewId+respondentPersonId) or a subject
   * with no resolvable reports for that reviewer type — not an error, just nothing new to create. */
  skippedCount: z.number().int().nonnegative(),
});

export const reactToFeedbackBodySchema = z.object({});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const feedbackContract = c.router(
  {
    listFeedback: {
      method: 'GET',
      path: '/',
      query: listFeedbackQuerySchema,
      responses: {
        200: paginatedSchema(feedbackSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        "List feedback — received (subjectPersonId), given (authorPersonId), or the org's public wall",
      metadata: routeMeta(null),
    },

    createFeedback: {
      method: 'POST',
      path: '/',
      body: createFeedbackBodySchema,
      responses: {
        201: feedbackSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Give feedback — DLP-scanned on write; a blocking verdict is a 422',
      metadata: routeMeta('feedback:give'),
    },

    listRequests: {
      method: 'GET',
      path: '/requests',
      query: listRequestsQuerySchema,
      responses: {
        200: paginatedSchema(feedbackRequestSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "List 360°-style feedback requests in the caller's visible scope",
      metadata: routeMeta(null),
    },

    createRequests: {
      method: 'POST',
      path: '/requests',
      body: createRequestsBodySchema,
      responses: {
        201: createRequestsResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Request feedback from N respondents in one transaction, one Idempotency-Key — never one row at a time',
      metadata: routeMeta(null),
    },

    getRequest: {
      method: 'GET',
      path: '/requests/:feedbackRequestId',
      responses: {
        200: feedbackRequestSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Get one feedback request',
      metadata: routeMeta(null),
    },

    submitRequestResponse: {
      method: 'POST',
      path: '/requests/:feedbackRequestId/respond',
      body: submitRequestResponseBodySchema,
      responses: {
        201: feedbackSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        "Respond to a feedback request — the Feedback insert and status: 'submitted' commit together, one transaction",
      metadata: routeMeta('feedback:give'),
    },

    declineRequest: {
      method: 'POST',
      path: '/requests/:feedbackRequestId/decline',
      body: declineRequestBodySchema,
      responses: {
        200: feedbackRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Decline a feedback request as the named respondent',
      metadata: routeMeta(null),
    },

    // Same method (POST) as createRequests but a different path shape ('/requests/upward-skip-
    // level', one static segment beyond '/requests') — no ordering hazard with getRequest's GET
    // '/requests/:feedbackRequestId' (different HTTP method, never competes for the same match).
    createUpwardSkipLevelRequests: {
      method: 'POST',
      path: '/requests/upward-skip-level',
      body: createUpwardSkipLevelRequestsBodySchema,
      responses: {
        201: createUpwardSkipLevelRequestsResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Create upward/skip-level FeedbackRequests for every direct report / grand-report of subjectPersonId, for whichever reviewer type(s) the cycle has enabled (PRF-13)',
      metadata: routeMeta(null),
    },

    // GET '/:feedbackId' MUST be registered after every static GET path above ('/', '/requests',
    // '/requests/:feedbackRequestId' — that one has an extra segment so it never conflicts) —
    // Express/ts-rest match routes in registration order, and a dynamic single-segment path
    // registered first would shadow 'GET /feedback/requests', matching "requests" as a feedbackId.
    // See leave.contract.ts's '/types' -> '/balances' -> '/requests' -> '/requests/:id' ordering for
    // the same convention.
    getFeedback: {
      method: 'GET',
      path: '/:feedbackId',
      responses: {
        200: feedbackSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Get one feedback entry',
      metadata: routeMeta(null),
    },

    reactToFeedback: {
      method: 'POST',
      path: '/:feedbackId/react',
      body: reactToFeedbackBodySchema,
      responses: {
        200: feedbackSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: '"Say Thanks" — increments the denormalized thanksCount',
      metadata: routeMeta(null),
    },
  },
  { pathPrefix: '/feedback' },
);
